import React from "react";
import * as Sentry from "@sentry/react";
import i18n from "i18n";
import { BUTTON_PRIMARY } from "components/Button/sleekButton";
import { cn } from "utils/cn";
import { sanitizeRoute } from "utils/privacy/telemetryUrl";

interface ErrorBoundaryProps {
  readonly children: JSX.Element | JSX.Element[];
}

const isChunkLoadError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.name === "ChunkLoadError" || error.message.includes("Loading chunk") || error.message.includes("Loading CSS chunk");
  }

  return false;
};

const CHUNK_RELOAD_KEY = "chunk_reload_attempted";

// With site data blocked, merely reading `window.sessionStorage` throws a SecurityError (Sentry 7816782090). This
// boundary wraps every route and has no boundary above it, so a throw from its own effect unmounted the whole app —
// a blank page on every route. The reload marker is best-effort: unreadable storage means "no marker".
const readReloadMarker = (): string | null => {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
  } catch {
    return null;
  }
};

/** Returns whether the marker was stored — without it a reload could loop, so the caller must not reload. */
const writeReloadMarker = (): boolean => {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");

    return true;
  } catch {
    return false;
  }
};

const clearReloadMarker = () => {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
};

const ErrorFallback = ({ error }: { error: unknown }) => {
  React.useEffect(() => {
    if (isChunkLoadError(error) && !readReloadMarker() && writeReloadMarker()) {
      window.location.reload();
    }
  }, [error]);

  return (
    <div className="flex h-screen w-full items-center justify-center bg-black">
      <div className="text-center">
        <h2 className="text-head4 mb-4 text-white">{i18n.t("common:error_boundary.title")}</h2>
        <p className="text-bodyMedium mb-4 text-grey">{i18n.t("common:error_boundary.body")}</p>
        <button className={cn("rounded-full px-4 py-2 text-black", BUTTON_PRIMARY)} onClick={() => window.location.reload()}>
          {i18n.t("common:error_boundary.reload")}
        </button>
      </div>
    </div>
  );
};

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  React.useEffect(() => {
    // Clear the reload guard once the app has booted without a chunk error
    // so a later deploy in the same session can also trigger auto-reload.
    clearReloadMarker();
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <ErrorFallback error={error} />}
      beforeCapture={(scope, error) => {
        if (isChunkLoadError(error)) {
          // ChunkLoadError is auto-recovered by reload; beforeSend already filters it.
          // Tag it so the filter has both a name and a source to match on.
          scope.setTag("error_source", "render");
          scope.setTag("chunk_load_error", "true");

          return;
        }
        scope.setTag("error_source", "render");
        // Tags are indexed and searchable — sanitised at the source, not only in beforeSend (REF-6708).
        scope.setTag("route", sanitizeRoute(`${window.location.pathname}${window.location.search}`));
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
